/**
 * Pesquisa livre do PNCP (api/search, a mesma do portal): procura no objeto, no órgão e nos itens, em todo o país.
 * Usada por "Buscar licitações" quando há texto no campo de pesquisa (D110). Libera acesso pelo navegador.
 */

/** Item do índice de pesquisa (só os campos usados). */
export interface AchadoPncp {
  numero_controle_pncp: string;
  title: string;
  description: string;
  numero: string | null;
  ano: string | null;
  orgao_cnpj: string;
  orgao_nome: string;
  unidade_codigo: string | null;
  unidade_nome: string | null;
  municipio_nome: string | null;
  uf: string;
  esfera_id: string | null;
  poder_id: string | null;
  modalidade_licitacao_id: string | null;
  modalidade_licitacao_nome: string | null;
  situacao_nome: string | null;
  /** No índice de pesquisa, o período de propostas vem em data_inicio_vigencia / data_fim_vigencia (Brasília, sem fuso). */
  data_inicio_vigencia: string | null;
  data_fim_vigencia: string | null;
  data_publicacao_pncp: string | null;
  valor_total_estimado: number | null;
  srp: boolean | null;
  informacao_complementar: string | null;
  link_sistema_origem: string | null;
}

export type SituacaoPesquisa = "recebendo_proposta" | "todos";
export const POR_PAGINA_PESQUISA = 20;

export async function pesquisarPncp(
  q: string,
  situacao: SituacaoPesquisa,
  ufs: string[],
  modalidades: number[],
  pagina: number,
): Promise<{ total: number; itens: AchadoPncp[] }> {
  const params = new URLSearchParams({
    q,
    tipos_documento: "edital",
    ordenacao: "relevancia",
    pagina: String(pagina),
    tam_pagina: String(POR_PAGINA_PESQUISA),
    status: situacao,
  });
  if (ufs.length) params.set("ufs", ufs.join("|"));
  if (modalidades.length) params.set("modalidades", modalidades.join("|"));
  let ultimoErro = "";
  // O índice do PNCP às vezes recusa a conexão: tenta de novo antes de desistir.
  for (let tentativa = 0; tentativa < 3; tentativa++) {
    try {
      const r = await fetch(`https://pncp.gov.br/api/search/?${params}`, { signal: AbortSignal.timeout(25000) });
      if (r.ok) {
        const d = (await r.json()) as { total?: number; items?: AchadoPncp[] };
        return { total: Number(d.total ?? 0), itens: d.items ?? [] };
      }
      ultimoErro = `HTTP ${r.status}`;
    } catch {
      ultimoErro = "sem resposta";
    }
    await new Promise((ok) => setTimeout(ok, 800 * (tentativa + 1)));
  }
  throw new Error(`O PNCP não respondeu à pesquisa (${ultimoErro}). Tente de novo em instantes.`);
}

/** Data e hora do PNCP (Brasília, sem fuso) → ISO com -03:00. */
export function dataHoraBrasilia(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const m = valor.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/);
  return m ? `${m[1]}${m[2] ?? ":00"}-03:00` : null;
}

const numeroDoAchado = (a: AchadoPncp) => (a.numero && a.ano ? `${a.numero}/${a.ano}` : a.title.replace(/^Edital( de [^n]+)? nº\s*/i, ""));

/**
 * Linha da cópia local (pncp_contratacao) a partir do achado: gravada só quando o usuário marca ou anota,
 * para a licitação aparecer em "Novas oportunidades" como as coletadas.
 */
export function linhaDoAchado(a: AchadoPncp) {
  const esfera = (a.esfera_id ?? "").trim();
  const poder = (a.poder_id ?? "").trim();
  return {
    numero_controle_pncp: a.numero_controle_pncp,
    uf: a.uf,
    municipio: a.municipio_nome,
    orgao_cnpj: a.orgao_cnpj,
    orgao_nome: a.orgao_nome,
    unidade_codigo: a.unidade_codigo,
    unidade_nome: a.unidade_nome,
    modalidade_id: Number(a.modalidade_licitacao_id ?? 0),
    modalidade_nome: a.modalidade_licitacao_nome ?? "",
    numero: numeroDoAchado(a),
    objeto: (a.description ?? "").trim() || "(sem objeto informado)",
    informacao_complementar: a.informacao_complementar,
    valor_total_estimado: a.valor_total_estimado || null,
    srp: Boolean(a.srp),
    data_abertura_proposta: dataHoraBrasilia(a.data_inicio_vigencia),
    data_encerramento_proposta: dataHoraBrasilia(a.data_fim_vigencia),
    data_publicacao: dataHoraBrasilia(a.data_publicacao_pncp),
    situacao: a.situacao_nome,
    link_sistema_origem: a.link_sistema_origem || null,
    esfera: /^[A-Z]$/.test(esfera) ? esfera : null,
    poder: /^[A-Z]$/.test(poder) ? poder : null,
  };
}

export { numeroDoAchado };
